import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import './Login.css';

// Presentación comercial. No modifica los permisos ni los planes configurados en el backend.
const COUNTRIES = [
  { code: 'EC', name: 'Ecuador' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Perú' },
  { code: 'BR', name: 'Brasil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'US', name: 'Estados Unidos' },
];

const LANGUAGES = [
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Português' },
  { code: 'en', name: 'English' },
];

const I18N = {
  es: {
    country: 'País', language: 'Idioma',
    heroTitle: 'Monitoreo inteligente para sus equipos e instalaciones.',
    heroDescription: 'Visualice temperatura, humedad y otras variables de sus procesos, equipos e instalaciones. Consulte lecturas, alertas y reportes desde una sola plataforma.',
    demoLabel: 'VISTA DEMOSTRATIVA', demoDevice: 'Refrigerador R-02', demoLocation: 'Ejemplo de monitoreo',
    demoStatus: 'Dentro de límites', demoTemp: 'Temperatura', demoHumidity: 'Humedad relativa',
    demoRange: 'Rango ilustrativo de 2 a 8 °C', demoPeriod: 'Gráfica ilustrativa de 24 h',
    featureTitles: ['Monitoreo de variables', 'Alertas configurables', 'Reportes y trazabilidad'],
    featureDescriptions: ['Visualice las lecturas de sus sensores.', 'Detecte valores fuera de los rangos definidos.', 'Consulte el historial y genere reportes.'],
    loginTitle: 'Iniciar sesión', loginSubtitle: 'Ingrese con el usuario asignado a su empresa.',
    username: 'Usuario', password: 'Contraseña', usernamePlaceholder: 'Ingrese su usuario',
    passwordPlaceholder: 'Ingrese su contraseña', show: 'Mostrar', hide: 'Ocultar',
    remember: 'Recordar mi usuario', submit: 'Iniciar sesión', submitting: 'Ingresando…',
    noAccount: '¿Aún no tiene cuenta?', createAccount: 'Crear cuenta', plansLink: 'Ver planes y precios',
    footer: '© 2026 ELICROM. Todos los derechos reservados.',
    networkError: 'No se recibió respuesta del servidor.', genericError: 'No se pudo iniciar sesión.',
    plansTitle: 'Planes de ELICROM IoT',
    plansIntro: 'Compare las opciones de monitoreo. Las funciones y tarifas especiales se confirman mediante cotización comercial.',
    countryInfo: 'País seleccionado', countryDisclaimer: 'La selección de país no cambia la cuenta ni la tarifa automáticamente.',
    free: 'Gratuita', standard: 'Estándar', professional: 'Profesional', corporate: 'Corporativo',
    freeDescription: 'Para conocer la plataforma.', standardDescription: 'Para operación y monitoreo continuo.',
    professionalDescription: 'Para proyectos con necesidades adicionales.', corporateDescription: 'Para proyectos multisede o integraciones.',
    recommended: 'Plan disponible', perVariableYear: 'por variable al año',
    quote: 'A consultar', tailored: 'A medida',
    freeBenefits: ['1 administrador', 'Hasta 3 equipos', '30 días de historial', 'Alertas por correo', 'Reportes estándar'],
    standardBenefits: ['Administradores y usuarios ilimitados', 'Equipos e historial ilimitados', 'Alertas por correo y notificaciones push', 'Avisos de calibración y batería', 'Reportes anuales y registro de eventos'],
    professionalBenefits: ['Configuración según los requerimientos del proyecto', 'Alcance y funcionalidades sujetos a evaluación', 'Propuesta comercial personalizada'],
    corporateBenefits: ['Evaluación de proyectos multisede', 'Posibles integraciones a medida', 'Condiciones sujetas a cotización'],
    freeAction: 'Crear cuenta gratis',
    calculatorTitle: 'Calcule una referencia de su inversión',
    calculatorIntro: 'Estimación del plan Estándar con la tarifa publicada actualmente: USD 7 por variable al año.',
    sensors: 'Cantidad de equipos', variables: 'Variables por equipo',
    calculatorResult: 'Estimación anual', annualUnit: 'al año',
    monthlyEquivalent: 'Equivalente mensual', estimateNotice: 'Valor orientativo en USD, sin impuestos. No incluye equipos físicos, instalación ni conectividad. La cotización final debe ser confirmada por ELICROM.',
    localPriceNotice: 'La tarifa usada es la referencia publicada en USD; consulte el precio aplicable en su país.',
    close: 'Cerrar',
  },
  en: {
    country: 'Country', language: 'Language',
    heroTitle: 'Smart monitoring for your equipment and facilities.',
    heroDescription: 'View temperature, humidity and other measurements from your processes, equipment and facilities. Access readings, alerts and reports on a single platform.',
    demoLabel: 'DEMONSTRATION', demoDevice: 'Refrigerator R-02', demoLocation: 'Monitoring example',
    demoStatus: 'Within limits', demoTemp: 'Temperature', demoHumidity: 'Relative humidity',
    demoRange: 'Illustrative range: 2 to 8 °C', demoPeriod: 'Illustrative 24-hour chart',
    featureTitles: ['Measurement monitoring', 'Configurable alerts', 'Reports and traceability'],
    featureDescriptions: ['View your sensor readings.', 'Detect readings outside defined limits.', 'Review history and generate reports.'],
    loginTitle: 'Sign in', loginSubtitle: 'Sign in using your company-assigned account.',
    username: 'Username', password: 'Password', usernamePlaceholder: 'Enter username',
    passwordPlaceholder: 'Enter password', show: 'Show', hide: 'Hide',
    remember: 'Remember my username', submit: 'Sign in', submitting: 'Signing in…',
    noAccount: 'New to the platform?', createAccount: 'Create account', plansLink: 'View plans and pricing',
    footer: '© 2026 ELICROM. All rights reserved.',
    networkError: 'The server did not respond.', genericError: 'Unable to sign in.',
    plansTitle: 'ELICROM IoT plans',
    plansIntro: 'Compare monitoring options. Additional features and special rates require a commercial quotation.',
    countryInfo: 'Selected country', countryDisclaimer: 'Changing the country does not automatically modify your account or rates.',
    free: 'Free', standard: 'Standard', professional: 'Professional', corporate: 'Corporate',
    freeDescription: 'Discover the platform.', standardDescription: 'For continuous operations and monitoring.',
    professionalDescription: 'For projects with additional requirements.', corporateDescription: 'For multisite projects and integrations.',
    recommended: 'Available plan', perVariableYear: 'per variable per year',
    quote: 'Contact us', tailored: 'Custom',
    freeBenefits: ['1 administrator', 'Up to 3 devices', '30 days of history', 'Email alerts', 'Standard reports'],
    standardBenefits: ['Unlimited administrators and users', 'Unlimited devices and history', 'Email and push alerts', 'Calibration and low-battery notices', 'Annual reports and event logs'],
    professionalBenefits: ['Configuration based on project requirements', 'Scope and features subject to review', 'Custom commercial proposal'],
    corporateBenefits: ['Multisite project assessment', 'Possible custom integrations', 'Terms subject to quotation'],
    freeAction: 'Create free account',
    calculatorTitle: 'Estimate your investment',
    calculatorIntro: 'Standard plan estimate using the currently published rate: USD 7 per variable per year.',
    sensors: 'Number of devices', variables: 'Variables per device',
    calculatorResult: 'Annual estimate', annualUnit: 'per year',
    monthlyEquivalent: 'Monthly equivalent', estimateNotice: 'Indicative USD estimate, excluding tax. Hardware, installation and connectivity are not included. ELICROM must confirm final pricing.',
    localPriceNotice: 'The USD amount is based on the published reference rate; request applicable local pricing.',
    close: 'Close',
  },
  pt: {
    country: 'País', language: 'Idioma',
    heroTitle: 'Monitoramento inteligente para seus equipamentos e instalações.',
    heroDescription: 'Visualize temperatura, umidade e outras variáveis dos seus processos, equipamentos e instalações. Acesse leituras, alertas e relatórios em uma única plataforma.',
    demoLabel: 'DEMONSTRAÇÃO', demoDevice: 'Refrigerador R-02', demoLocation: 'Exemplo de monitoramento',
    demoStatus: 'Dentro dos limites', demoTemp: 'Temperatura', demoHumidity: 'Umidade relativa',
    demoRange: 'Faixa ilustrativa de 2 a 8 °C', demoPeriod: 'Gráfico ilustrativo de 24 h',
    featureTitles: ['Monitoramento de variáveis', 'Alertas configuráveis', 'Relatórios e rastreabilidade'],
    featureDescriptions: ['Visualize as leituras dos sensores.', 'Detecte valores fora dos limites definidos.', 'Consulte o histórico e gere relatórios.'],
    loginTitle: 'Entrar', loginSubtitle: 'Entre com o usuário atribuído à sua empresa.',
    username: 'Usuário', password: 'Senha', usernamePlaceholder: 'Digite seu usuário',
    passwordPlaceholder: 'Digite sua senha', show: 'Mostrar', hide: 'Ocultar',
    remember: 'Lembrar meu usuário', submit: 'Entrar', submitting: 'Entrando…',
    noAccount: 'Ainda não tem uma conta?', createAccount: 'Criar conta', plansLink: 'Ver planos e preços',
    footer: '© 2026 ELICROM. Todos os direitos reservados.',
    networkError: 'O servidor não respondeu.', genericError: 'Não foi possível entrar.',
    plansTitle: 'Planos ELICROM IoT',
    plansIntro: 'Compare as opções de monitoramento. Recursos e preços especiais exigem uma proposta comercial.',
    countryInfo: 'País selecionado', countryDisclaimer: 'Selecionar outro país não altera automaticamente sua conta nem os preços.',
    free: 'Gratuito', standard: 'Padrão', professional: 'Profissional', corporate: 'Corporativo',
    freeDescription: 'Para conhecer a plataforma.', standardDescription: 'Para monitoramento e operação contínuos.',
    professionalDescription: 'Para projetos com necessidades adicionais.', corporateDescription: 'Para projetos com várias unidades.',
    recommended: 'Plano disponível', perVariableYear: 'por variável ao ano',
    quote: 'Sob consulta', tailored: 'Personalizado',
    freeBenefits: ['1 administrador', 'Até 3 dispositivos', '30 dias de histórico', 'Alertas por e-mail', 'Relatórios padrão'],
    standardBenefits: ['Administradores e usuários ilimitados', 'Dispositivos e histórico ilimitados', 'Alertas por e-mail e notificações push', 'Avisos de calibração e bateria', 'Relatórios anuais e registro de eventos'],
    professionalBenefits: ['Configuração conforme os requisitos do projeto', 'Escopo e funções sujeitos à avaliação', 'Proposta comercial personalizada'],
    corporateBenefits: ['Avaliação de projetos com várias unidades', 'Possíveis integrações personalizadas', 'Condições sujeitas a orçamento'],
    freeAction: 'Criar conta grátis',
    calculatorTitle: 'Estime seu investimento',
    calculatorIntro: 'Estimativa do plano Padrão com a tarifa atualmente publicada: USD 7 por variável ao ano.',
    sensors: 'Número de dispositivos', variables: 'Variáveis por dispositivo',
    calculatorResult: 'Estimativa anual', annualUnit: 'por ano',
    monthlyEquivalent: 'Equivalente mensal', estimateNotice: 'Valor indicativo em USD, sem impostos. Não inclui hardware, instalação ou conectividade. A ELICROM deve confirmar o preço final.',
    localPriceNotice: 'O valor em USD usa a tarifa de referência publicada; consulte o preço para seu país.',
    close: 'Fechar',
  },
};

const ICON_PATHS = {
  user: <><path d="M20 21a8 8 0 0 0-16 0"/><circle cx="12" cy="8" r="4"/></>,
  lock: <><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></>,
  eye: <><path d="M2 12s3.7-7 10-7 10 7 10 7-3.7 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></>,
  eyeOff: <><path d="m3 3 18 18"/><path d="M10.6 5.1A11 11 0 0 1 12 5c6.3 0 10 7 10 7a14.7 14.7 0 0 1-3.2 3.9M6 6.2C3.5 8 2 12 2 12s3.7 7 10 7a10.4 10.4 0 0 0 4.1-.8"/><path d="M10 10a3 3 0 0 0 4 4"/></>,
  activity: <><path d="M2 12h4l3-7 5 14 3-7h5"/></>,
  bell: <><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"/></>,
  report: <><path d="M6 2h8l5 5v15H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z"/><path d="M14 2v6h5M8 13h8M8 17h8"/></>,
  check: <><path d="m4 12 5 5L20 6"/></>,
  close: <><path d="M18 6 6 18M6 6l12 12"/></>,
  arrow: <><path d="M5 12h14M13 6l6 6-6 6"/></>,
};

const Icon = ({ name, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    {ICON_PATHS[name]}
  </svg>
);

const readRememberedUser = () => {
  try { return window.localStorage.getItem('elicrom_iot_remembered_username') || ''; }
  catch { return ''; }
};

const currency = (value, lang) => new Intl.NumberFormat(
  lang === 'pt' ? 'pt-BR' : lang === 'en' ? 'en-US' : 'es-EC',
  { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }
).format(value);

const PLANS = [
  { id: 'free', benefits: 'freeBenefits', action: 'freeAction' },
  { id: 'standard', benefits: 'standardBenefits', featured: true },
  { id: 'professional', benefits: 'professionalBenefits' },
  { id: 'corporate', benefits: 'corporateBenefits' },
];

const PlansModal = ({ t, language, country, onClose, onCreateAccount }) => {
  const closeButtonRef = useRef(null);
  const [deviceCount, setDeviceCount] = useState(10);
  const [variablesPerDevice, setVariablesPerDevice] = useState(2);
  const annualEstimate = deviceCount * variablesPerDevice * 7;
  const selectedCountry = COUNTRIES.find((item) => item.code === country)?.name || 'Ecuador';

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const setBoundedDevices = (next) => setDeviceCount(Math.min(500, Math.max(1, Number(next) || 1)));
  const setBoundedVariables = (next) => setVariablesPerDevice(Math.min(16, Math.max(1, Number(next) || 1)));

  return (
    <div className="iot-overlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="iot-modal" role="dialog" aria-modal="true" aria-labelledby="iot-plans-title">
        <div className="iot-modal-header">
          <div>
            <p className="iot-eyebrow iot-eyebrow-light">ELICROM IoT</p>
            <h2 id="iot-plans-title">{t.plansTitle}</h2>
            <p>{t.plansIntro}</p>
          </div>
          <button ref={closeButtonRef} className="iot-icon-button" type="button" onClick={onClose} aria-label={t.close}>
            <Icon name="close" />
          </button>
        </div>

        <div className="iot-modal-location">
          <strong>{t.countryInfo}: {selectedCountry}</strong>
          <span>{t.countryDisclaimer}</span>
        </div>

        <div className="iot-plans-grid">
          {PLANS.map((plan) => (
            <article key={plan.id} className={`iot-plan-card ${plan.featured ? 'iot-plan-featured' : ''}`}>
              {plan.featured && <span className="iot-plan-badge">{t.recommended}</span>}
              <h3>{t[plan.id]}</h3>
              <p className="iot-plan-description">{t[`${plan.id}Description`]}</p>
              <div className="iot-plan-price">
                {plan.id === 'free' ? '$0' : plan.id === 'standard' ? '$7' : plan.id === 'corporate' ? t.tailored : t.quote}
              </div>
              {plan.id === 'standard' && <p className="iot-plan-unit">{t.perVariableYear}</p>}
              <ul>
                {t[plan.benefits].map((benefit) => (
                  <li key={benefit}><Icon name="check" size={16} /><span>{benefit}</span></li>
                ))}
              </ul>
              {plan.id === 'free' ? (
                <button className="iot-plan-button" type="button" onClick={onCreateAccount}>{t[plan.action]}</button>
              ) : (
                <span
                  className="iot-plan-button"
                  style={{
                    background: 'transparent',
                    borderColor: 'transparent',
                    color: '#215b96',
                    cursor: 'text',
                    fontSize: '14px',
                    fontWeight: 700,
                  }}
                >
                  iot@elicrom.com
                </span>
              )}
            </article>
          ))}
        </div>

        <section className="iot-calculator" aria-labelledby="iot-calculator-title">
          <div className="iot-calculator-controls">
            <h3 id="iot-calculator-title">{t.calculatorTitle}</h3>
            <p>{t.calculatorIntro}</p>
            <label htmlFor="iot-device-count">{t.sensors}</label>
            <div className="iot-number-control">
              <input id="iot-device-count" type="range" min="1" max="500" value={deviceCount}
                onChange={(event) => setBoundedDevices(event.target.value)} />
              <input type="number" min="1" max="500" value={deviceCount} aria-label={t.sensors}
                onChange={(event) => setBoundedDevices(event.target.value)} />
            </div>
            <label htmlFor="iot-variables-count">{t.variables}</label>
            <select id="iot-variables-count" value={variablesPerDevice}
              onChange={(event) => setBoundedVariables(event.target.value)}>
              {Array.from({ length: 16 }, (_, index) => index + 1).map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </div>
          <div className="iot-calculator-result">
            <span>{t.calculatorResult}</span>
            <strong>{currency(annualEstimate, language)}</strong>
            <small>{t.annualUnit}</small>
            <div className="iot-result-divider" />
            <span>{t.monthlyEquivalent}</span>
            <b>{currency(annualEstimate / 12, language)}</b>
          </div>
        </section>
        <p className="iot-price-disclaimer">{t.estimateNotice} {country !== 'EC' ? t.localPriceNotice : ''}</p>
      </div>
    </div>
  );
};

const Login = () => {
  const [username, setUsername] = useState(readRememberedUser);
  const [password, setPassword] = useState('');
  const [rememberUsername, setRememberUsername] = useState(() => Boolean(readRememberedUser()));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [country, setCountry] = useState('EC');
  const [language, setLanguage] = useState('es');
  const [loginError, setLoginError] = useState('');
  const { login: authLogin } = useContext(AuthContext);
  const t = I18N[language];

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      // Se conserva exactamente el endpoint y el contrato de la autenticación actual.
      const response = await axios.post(
        '/api/auth/login',
        { username, password },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.pgrest_token) {
        localStorage.setItem('pgrest_token', response.data.pgrest_token);
      }
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      if (rememberUsername) {
        localStorage.setItem('elicrom_iot_remembered_username', username);
      } else {
        localStorage.removeItem('elicrom_iot_remembered_username');
      }
      authLogin(response.data.token, response.data.user || null);
    } catch (error) {
      const serverMessage = error.response?.data?.message;
      setLoginError(typeof serverMessage === 'string' && serverMessage.trim()
        ? serverMessage
        : error.request && !error.response ? t.networkError : t.genericError);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = () => {
    window.location.href = '/registro';
  };

  return (
    <div className="iot-login" lang={language}>
      <header className="iot-topbar">
        <div className="iot-topbar-content">
          <div className="iot-topbar-brand" aria-label="ELICROM IoT">
            <span className="iot-topbar-logo"><img src="/elicrom-logo.png" alt="ELICROM" /></span>
            <span className="iot-topbar-wordmark">IoT <span>MONITOREO</span></span>
          </div>
          <div className="iot-topbar-settings">
            <label className="iot-setting">
              <span>{t.country}</span>
              <select value={country} onChange={(event) => setCountry(event.target.value)} aria-label={t.country}>
                {COUNTRIES.map(({ code, name }) => <option key={code} value={code}>{name}</option>)}
              </select>
            </label>
            <label className="iot-setting">
              <span>{t.language}</span>
              <select value={language} onChange={(event) => setLanguage(event.target.value)} aria-label={t.language}>
                {LANGUAGES.map(({ code, name }) => <option key={code} value={code}>{name}</option>)}
              </select>
            </label>
          </div>
        </div>
      </header>

      <main className="iot-main">
        <div className="iot-panel">
          <section className="iot-hero" aria-labelledby="iot-hero-title">
            <div className="iot-hero-inner">
              <h1 id="iot-hero-title">{t.heroTitle}</h1>
              <p className="iot-hero-intro">{t.heroDescription}</p>

              <div className="iot-demo-card">
                <div className="iot-demo-head">
                  <div><span className="iot-demo-overline">{t.demoLabel}</span>
                    <strong>{t.demoDevice}</strong><small>{t.demoLocation}</small></div>
                  <span className="iot-demo-status"><i />{t.demoStatus}</span>
                </div>
                <div className="iot-readings">
                  <div><strong>5.9 <span>°C</span></strong><small>{t.demoTemp}</small></div>
                  <div><strong>64 <span>%</span></strong><small>{t.demoHumidity}</small></div>
                </div>
                <p className="iot-demo-range">{t.demoRange}</p>
                <div className="iot-chart" aria-label={t.demoPeriod} role="img">
                  <svg viewBox="0 0 500 90" preserveAspectRatio="none" aria-hidden="true">
                    <defs>
                      <linearGradient id="iot-demo-fill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#39b7ee" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="#39b7ee" stopOpacity="0.01" />
                      </linearGradient>
                    </defs>
                    <path d="M0 47 L15 48 L30 44 L45 46 L60 46 L75 43 L90 45 L105 44 L120 46 L135 43 L150 45 L165 44 L180 45 L195 43 L210 44 L225 41 L240 43 L255 41 L270 42 L285 40 L300 42 L315 45 L330 44 L345 45 L360 43 L375 45 L390 42 L405 44 L420 43 L435 45 L450 42 L465 44 L480 43 L500 41 L500 90 L0 90 Z" fill="url(#iot-demo-fill)" />
                    <path className="iot-chart-line" d="M0 47 L15 48 L30 44 L45 46 L60 46 L75 43 L90 45 L105 44 L120 46 L135 43 L150 45 L165 44 L180 45 L195 43 L210 44 L225 41 L240 43 L255 41 L270 42 L285 40 L300 42 L315 45 L330 44 L345 45 L360 43 L375 45 L390 42 L405 44 L420 43 L435 45 L450 42 L465 44 L480 43 L500 41" fill="none" stroke="#55c4fa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="iot-chart-caption">{t.demoPeriod}</div>
              </div>

              <div className="iot-hero-features">
                {t.featureTitles.map((featureTitle, index) => (
                  <div className="iot-hero-feature" key={index}>
                    <div className="iot-feature-icon"><Icon name={['activity', 'bell', 'report'][index]} size={19} /></div>
                    <div><strong>{featureTitle}</strong><p>{t.featureDescriptions[index]}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="iot-auth" aria-labelledby="iot-login-title">
            <div className="iot-auth-inner">
              <h2 id="iot-login-title">{t.loginTitle}</h2>
              <p className="iot-auth-intro">{t.loginSubtitle}</p>
              <form className="iot-form" onSubmit={handleLogin}>
                <label htmlFor="iot-login-username">{t.username}</label>
                <div className="iot-input-wrap">
                  <Icon name="user" size={19} />
                  <input id="iot-login-username" type="text" value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder={t.usernamePlaceholder} autoComplete="username" autoFocus required />
                </div>

                <label htmlFor="iot-login-password">{t.password}</label>
                <div className="iot-input-wrap">
                  <Icon name="lock" size={19} />
                  <input id="iot-login-password" type={showPassword ? 'text' : 'password'}
                    value={password} onChange={(event) => setPassword(event.target.value)}
                    placeholder={t.passwordPlaceholder} autoComplete="current-password" required />
                  <button className="iot-visibility" type="button" onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t.hide : t.show} aria-pressed={showPassword}>
                    <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
                    <span>{showPassword ? t.hide : t.show}</span>
                  </button>
                </div>

                <label className="iot-remember">
                  <input type="checkbox" checked={rememberUsername}
                    onChange={(event) => setRememberUsername(event.target.checked)} />
                  <span>{t.remember}</span>
                </label>

                {loginError && <div className="iot-login-error" role="alert">{loginError}</div>}
                <button className="iot-submit" type="submit" disabled={loading} aria-busy={loading}>
                  {loading ? t.submitting : t.submit}
                  {!loading && <Icon name="arrow" size={19} />}
                </button>
              </form>

              <div className="iot-signup-divider"><span>{t.noAccount}</span></div>
              <button className="iot-create" type="button" onClick={handleCreateAccount}>{t.createAccount}</button>
              <button className="iot-plans-link" type="button" onClick={() => setShowPlans(true)}>
                {t.plansLink} <Icon name="arrow" size={15} />
              </button>
              <p className="iot-footer">{t.footer}</p>
            </div>
          </section>
        </div>
      </main>

      {showPlans && (
        <PlansModal t={t} language={language} country={country}
          onClose={() => setShowPlans(false)} onCreateAccount={handleCreateAccount} />
      )}
    </div>
  );
};

export default Login;
